import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faLock } from "@fortawesome/free-solid-svg-icons";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { setReferralCode, getReferralCode } from '../utils/referralCodeStorage';
import './Login.css';

// Functional component for the Login Page
function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true); // Default to true for better UX
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { isDarkMode } = useTheme();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Check for referral code in URL on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');

    if (code) {
      // Store referral code in localStorage with expiration
      setReferralCode(code);
      console.log('Referral code detected on login and stored:', code);
    }
  }, [location]);

  // Surface OAuth errors that the server appended to the redirect URL
  // (e.g. /?error=oauth_exchange_failed after a failed Google/Apple callback).
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const oauthError = searchParams.get('error');
    if (!oauthError) return;

    const errorMessages = {
      oauth_exchange_failed: 'We could not complete sign-in with that provider. Please try again.',
      oauth_failed: 'Sign-in failed. Please try again.',
      oauth_unconfigured: 'This sign-in option is temporarily unavailable.',
      invalid_state: 'Your sign-in session expired. Please try again.',
      missing_code: 'The provider did not return an authorization code. Please try again.',
      access_denied: 'You cancelled sign-in. You can try again any time.',
    };
    setError(errorMessages[oauthError] || 'Sign-in failed. Please try again.');

    // Strip the error from the URL so a refresh does not re-show it.
    navigate(location.pathname, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      setIsLoading(false);
      return;
    }

    try {
      await login(email, password, rememberMe);
      // Navigation will happen automatically due to useEffect above
    } catch (err) {
      if (err && err.code === 'UserPending') {
        navigate(`/verify-email?email=${encodeURIComponent(err.email || email)}`);
        return;
      }
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOAuthLogin = (provider) => {
    setError('');

    const providerKey = provider.toLowerCase();
    const referralCode = getReferralCode();
    const query = referralCode ? `?referralCode=${encodeURIComponent(referralCode)}` : '';

    window.location.href = `${process.env.REACT_APP_API_BASE_URL}/api/session/${providerKey}/start${query}`;
  };

  // Theme-specific styles
  const themeStyles = useMemo(() => {
    return {
      container: {
        background: isDarkMode 
          ? 'linear-gradient(180deg, #0e1626 0%, #0b1220 100%)'
          : '#eceff4'
      },
      content: {
        background: isDarkMode 
          ? 'linear-gradient(145deg, #151e30 0%, #121a2c 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f5f9ff 100%)',
        boxShadow: isDarkMode 
          ? '0 6px 12px rgba(0, 0, 0, 0.3)' 
          : '0 6px 12px rgba(0, 0, 0, 0.1)'
      },
      text: {
        primary: isDarkMode ? '#ffffff' : '#2c3e50',
        secondary: isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'
      },
      button: {
        background: isDarkMode ? '#0074cc' : '#1976d2',
        color: '#ffffff'
      },
      link: {
        color: isDarkMode ? '#90cdf4' : '#3498db'
      },
      socialIcon: {
        google: '#DB4437',
        apple: isDarkMode ? '#FFFFFF' : '#000000',
        facebook: '#4267B2'
      }
    };
  }, [isDarkMode]);

  return (
    <div className={`login-page-container ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={themeStyles.container}>
      <div className="login-container-main" style={themeStyles.content}>
        <div className="login-header">
          <img 
            src={isDarkMode ? '/dark_green.png' : '/white_black.png'} 
            alt="ExamFlip Logo" 
            className="header-logo"
          />        </div>
        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message" style={{ 
              color: '#dc3545', 
              marginBottom: '15px', 
              padding: '10px', 
              borderRadius: '4px',
              backgroundColor: isDarkMode ? 'rgba(220, 53, 69, 0.1)' : 'rgba(220, 53, 69, 0.05)',
              border: '1px solid rgba(220, 53, 69, 0.3)',
              textAlign: 'center'
            }}>
              {error}
            </div>
          )}
          <div className="input-group">
            <FontAwesomeIcon 
              icon={faUser} 
              className={`input-icon ${isDarkMode ? 'dark-mode-icon' : 'light-mode-icon'}`}
            />
            <input
              type="email"
              placeholder="Email"
              className="auth-input"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="input-group">
            <FontAwesomeIcon 
              icon={faLock} 
              className={`input-icon ${isDarkMode ? 'dark-mode-icon' : 'light-mode-icon'}`}
            />
            <input
              type="password"
              placeholder="Password"
              className="auth-input"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>          <div className="remember-forgot">
            <div className="remember-me">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={() => setRememberMe(!rememberMe)}
              />
              <label htmlFor="rememberMe" className="remember-me-label">Keep me signed in</label>
            </div>
            <Link to="/reset-password" className="forgot-password">
              Forgot password?
            </Link>
          </div><button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
            style={{
              background: isLoading ? '#ccc' : themeStyles.button.background,
              color: themeStyles.button.color,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? 'Logging In...' : 'Log In'}
          </button>
        </form>
        <div className="social-login-options">
          <p style={{ color: themeStyles.text.secondary, textAlign: 'center', marginBottom: '15px' }}>
            Or sign in with
          </p>
          <div className="social-icons" style={{ justifyContent: 'center' }}>
            <button 
              className="google-signin-button"
              onClick={() => handleOAuthLogin('Google')}
              type="button"
            >
              <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Continue with Google</span>
            </button>
            <button
              className="apple-signin-button"
              onClick={() => handleOAuthLogin('Apple')}
              type="button"
            >
              <FontAwesomeIcon icon={faApple} style={{ fontSize: '18px' }} />
              <span>Continue with Apple</span>
            </button>
          </div>
        </div>
        <div className="register-now">
          <p style={{ color: themeStyles.text.primary }}>
            Don't have an account? <Link to="/create-account" style={{ color: themeStyles.link.color }}>Register now</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
